import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import InnerTitle2 from "../../components/InnerTitle2";
import { getNotifications, markNotificationAsRead } from "../../services/stream.service";
import moment from "moment";
import { useNavigate } from "react-router-dom";

// Define the pink color used for the icon background
const PINK_COLOR = "#ffc0cb";
const RED_ICON_COLOR = "#ff69b4";

// --- Reusable Notification Item Component ---
const NotificationItem = ({ icon, title, description, time, isRead, onClick }) => {
  return (
    <div
      className={`flex items-start justify-between p-[20px] mb-3 rounded-[10px] shadow-[0px_2px_24px_0px_rgba(0,0,0,0.05)] border-1 border-[#F4F4F5] cursor-pointer ${isRead ? "bg-white opacity-70" : "bg-zinc-50 border-pink-200"}`}
      onClick={onClick}
    >
      {/* Left Section: Icon and Text Content */}
      <div className="flex items-start space-x-2">
        {/* Icon Container (Heart/Flower Icon Simulation) */}
        <div className="w-[45px] md:w-[52px] flex items-center justify-center rounded-full flex-shrink-0">
          <img src="/images/bell1.png" alt="bell" />
        </div>

        {/* Text Content */}
        <div className="flex flex-col gap-[5px]">
          <h3 className={`headings-h3 ${isRead ? "text-zinc-600" : "text-zinc-800 font-bold"}`}>{title}</h3>
          <p className="body-lg text-gray-400 mt-0.5">{description}</p>
        </div>
      </div>

      {/* Right Section: Timestamp */}
      <div className="flex-shrink-0 ml-4 pt-1">
        <span className="body-2 text-gray-500">{time}</span>
      </div>
    </div>
  );
};

// --- Main Notifications Component Updated ---
function Notifications() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const navigate = useNavigate();
  const limit = 20;

  useEffect(() => {
    fetchNotifications(1);
  }, []);

  const fetchNotifications = async (currentPage) => {
    try {
      if (currentPage === 1) setLoading(true);
      else setLoadingMore(true);

      const res = await getNotifications(currentPage, limit);
      if (res.data && res.data.notifications) {
        if (currentPage === 1) {
          setNotifications(res.data.notifications);
        } else {
          setNotifications(prev => [...prev, ...res.data.notifications]);
        }
        setTotalPages(res.data.totalPages || 1);
      } else if (res.data) {
        if (Array.isArray(res.data)) {
          if (currentPage === 1) {
            setNotifications(res.data);
          } else {
            setNotifications(prev => [...prev, ...res.data]);
          }
          setTotalPages(1);
        }
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      if (currentPage === 1) setLoading(false);
      else setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (page < totalPages) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchNotifications(nextPage);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await markNotificationAsRead(id);
      setNotifications(prev =>
        prev.map(notif => notif._id === id ? { ...notif, is_read: true } : notif)
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await handleMarkAsRead(notification._id);
    }

    if (notification.type === "STAFF_INVITED") {
      navigate("/user/staff-invitations");
    } else if (notification.type === "STAFF_INVITE_ACCEPTED") {
      navigate("/advertiser/my-team");
    } else if (notification.type === "STREAM_REMINDER") {
      const streamId = notification.metadata?.stream_id;
      const streamDetails = notification.stream_details;


      const roomId = notification.metadata?.room_id || streamDetails?.room_id;
      const publisherId = notification.metadata?.publisher_id || streamDetails?.seller_id;

      if (publisherId && roomId) {
        navigate(`/user/live-streams/${publisherId}/${roomId}/${streamId}`);
      } else {
        console.error("Missing publisherId or roomId to navigate to live stream.", notification);
      }

    }
  };

  return (
    <>
      <div className="notifications-bg py-10 min-h-screen">
        <div className="container">
          {/* Header */}
          <h1 className="headings-h1 text-zinc-950 border-b border-gray-200 pb-[15px] mb-[30px]">
            {t('advertiser.notifications.heading')}
          </h1>

          {/* Notifications List */}
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-10">
                <p className="text-gray-500 italic">{t('advertiser.notifications.loading')}</p>
              </div>
            ) : notifications.length > 0 ? (
              <>
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification._id}
                    title={notification.title}
                    description={notification.description}
                    time={moment(notification.createdAt).fromNow()}
                    isRead={notification.is_read}
                    onClick={() => handleNotificationClick(notification)}
                  />
                ))}
                {page < totalPages && (
                  <div className="flex justify-center mt-6">
                    <button
                      className="px-8 py-2.5 bg-[#ff69b4] text-white rounded-full font-medium shadow-md hover:bg-pink-600 transition disabled:opacity-75 disabled:cursor-not-allowed"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore ? t('advertiser.notifications.loading') : t('advertiser.notifications.load_more')}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20 bg-white rounded-[10px] shadow-sm">
                <img src="/images/bell1.png" alt="No Notifications" className="mx-auto mb-4 opacity-30 w-16" />
                <p className="text-gray-500 text-lg">{t('advertiser.notifications.no_notifications')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Notifications;
